import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import Button from "./Button";
import Icon from "./Icon";
import "./SplitButton.scss";

// Fontaine M13 — SplitButton. Visual contract: Style Guide V2 `split-button` (one pill, primary half +
// caret half, whole-pill hover). Composed from Button; the caret opens a menu of secondary actions.

/**
 * @param {ReactNode} children                  main action label
 * @param {() => void} onClick                  main action
 * @param {{label: string, onSelect: () => void, icon?: ReactNode, danger?: boolean, disabled?: boolean}[]} items
 * @param {"primary"|"secondary"} [variant="primary"]
 * @param {"sm"|"md"|"lg"|"xl"} [size="md"]
 * @param {string} [menuLabel="More options"]    accessible name of the caret half
 */
const SplitButton = forwardRef(function SplitButton(
  { children, onClick, items = [], variant = "primary", size = "md", menuLabel = "More options", disabled = false, loading = false, fullWidth = false, className = "", ...rest },
  ref
) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const classes = ["ft-split", `ft-split--${variant}`, open ? "is-open" : "", fullWidth ? "ft-split--full" : "", className].filter(Boolean).join(" ");

  return (
    <span className={classes} ref={rootRef} {...rest}>
      <Button ref={ref} variant={variant} size={size} onClick={onClick} disabled={disabled} loading={loading} className="ft-split__main">
        {children}
      </Button>
      <Button
        variant={variant}
        size={size}
        disabled={disabled || loading}
        className="ft-split__caret"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={menuLabel}
        onClick={() => setOpen((o) => !o)}
        leadingIcon={<Icon name="ChevronDown" size="sm" />}
      />
      {open ? (
        <ul className="ft-split__menu" role="menu" id={menuId}>
          {items.map((item, i) => (
            <li key={item.label ?? i} role="none">
              <button
                type="button"
                role="menuitem"
                className={`ft-split__item${item.danger ? " is-danger" : ""}`}
                disabled={item.disabled}
                onClick={() => { setOpen(false); item.onSelect?.(); }}
              >
                {item.icon ? <span className="ft-split__item-icon" aria-hidden="true">{item.icon}</span> : null}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
});

export default SplitButton;
